/**
 * The 3D plan editor's pick candidates and footprints (`spec-onboarding-3d-plan-edit-machines`,
 * R1.8 and R2.2), from the plan's items as drawn and the boxes the drawn machines registered, and
 * its wall candidates (`spec-onboarding-3d-plan-edit-walls`, R2), from the walls as drawn.
 * Pure, so the pick and the driver read one answer.
 */
import type { FloorPlannerItem, FloorPlannerPoint, FloorPlannerWall } from '@repo/frontend-types';
import type { PlanSceneFootprint, WallEnd } from '@repo/ui/planner';
import { floorWallOutlines } from '@repo/ui';
import type { PlanMachineBox } from '../twinEntities/asset/PlanEditorMachineContext';
import type { PlanPickCandidate, PlanPickOutline, PlanPickWall, PlanPickWallEnd } from './planPick';

/** A machine with nothing registered yet (its model still loading): no area at all. */
export const NO_FOOTPRINT: PlanSceneFootprint = { width: 0, depth: 0, offsetX: 0, offsetY: 0 };

export const footprintOfBox = (box: PlanMachineBox | undefined): PlanSceneFootprint =>
  box
    ? { width: box.width, depth: box.depth, offsetX: box.offsetX, offsetY: box.offsetY }
    : NO_FOOTPRINT;

/** The box's centre on the plan: the item's point plus its offset, turned as the item is. */
export const boxCentre = (
  item: { x: number; y: number; rotation?: number | null },
  box: Pick<PlanMachineBox, 'offsetX' | 'offsetY'>,
): { x: number; y: number } => {
  const turn = ((item.rotation ?? 0) * Math.PI) / 180;
  const cos = Math.cos(turn);
  const sin = Math.sin(turn);
  return {
    x: item.x + box.offsetX * cos - box.offsetY * sin,
    y: item.y + box.offsetX * sin + box.offsetY * cos,
  };
};

/**
 * The box's outline as the pick reads it (`plan-machine-outline`, R4): its loops less the box's offset,
 * so relative to the box centre the candidate is turned about, with their bounds. `undefined` where
 * the box has none yet or none at all, so the box holds the press (R4.2).
 */
export const pickOutlineOf = (box: PlanMachineBox): PlanPickOutline | undefined => {
  const loops = (box.outline ?? []).filter(loop => loop.length >= 3);
  if (loops.length === 0) return undefined;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  const relative = loops.map(loop =>
    loop.map(p => {
      const x = p.x - box.offsetX;
      const y = p.y - box.offsetY;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
      return { x, y };
    }),
  );
  return { loops: relative, minX, maxX, minY, maxY };
};

/**
 * Every drawn machine with a registered box, as the pick reads it: `_deleted` ones are not drawn and
 * are left out (R1.3), and a machine still loading has registered nothing (R2.2).
 */
export const planPickCandidates = (
  items: readonly FloorPlannerItem[],
  boxOf: (refid: string) => PlanMachineBox | undefined,
  itemShown: (item: FloorPlannerItem) => FloorPlannerItem,
): PlanPickCandidate[] => {
  const candidates: PlanPickCandidate[] = [];
  items.forEach((stored, order) => {
    if (stored._deleted) return;
    const box = boxOf(stored.refid);
    if (!box) return;
    const item = itemShown(stored);
    candidates.push({
      refid: item.refid,
      centre: boxCentre(item, box),
      halfWidth: box.width / 2,
      halfDepth: box.depth / 2,
      rotation: item.rotation ?? 0,
      top: box.top,
      order,
      outline: pickOutlineOf(box),
    });
  });
  return candidates;
};

/** The floor's joined outlines for the walls last asked about: a pick per pointer event reuses them. */
const outlinesFor = new WeakMap<readonly FloorPlannerWall[], FloorPlannerPoint[][]>();

/**
 * Every wall with an id on the floor as the pick reads it (`edit-walls`, R2.2): its centre line as drawn and half
 * its thickness, never below 0, so a wall with none is picked by the tolerance alone, and its joined
 * outline as drawn (`wall-editing-look`, R1), so a sharp corner's bevel past the band is picked too.
 * Launch keeps a `_deleted` wall and the scene draws it, so it is a candidate too.
 */
export const planWallCandidates = (walls: readonly FloorPlannerWall[]): PlanPickWall[] => {
  let outlines = outlinesFor.get(walls);
  if (!outlines) {
    outlines = floorWallOutlines(walls);
    outlinesFor.set(walls, outlines);
  }
  const joined = outlines;
  return walls.flatMap((wall, order) =>
    wall.id === undefined
      ? []
      : [
          {
            refid: wall.id,
            order,
            a: wall.a,
            b: wall.b,
            halfThickness: Math.max(0, wall.thickness / 2),
            outline: joined[order],
          },
        ],
  );
};

/**
 * The shown ends of the walls `owners` names (`wallEndsShownFor`'s answer, R5.1), where they are
 * drawn: two per wall, `a` then `b`.
 */
export const planWallEndCandidates = (
  walls: readonly FloorPlannerWall[],
  owners: readonly string[],
): PlanPickWallEnd[] =>
  owners.flatMap(refid => {
    const order = walls.findIndex(w => w.id === refid);
    const wall = walls[order];
    if (!wall) return [];
    return [
      { refid, order, index: 0 as const, point: wall.a },
      { refid, order, index: 1 as const, point: wall.b },
    ];
  });

/**
 * The walls with a dragged corner's ends at `point` (R5.3): a moved wall is a new object, every other
 * wall the same object, so the scene converts only the walls that moved.
 */
export const wallsWithCorner = (
  walls: readonly FloorPlannerWall[],
  joint: readonly WallEnd[],
  point: FloorPlannerPoint,
): readonly FloorPlannerWall[] =>
  walls.map(wall => {
    const moved = joint.filter(e => e.id === wall.id);
    if (moved.length === 0) return wall;
    const next = { ...wall };
    for (const { end } of moved) next[end] = { x: point.x, y: point.y };
    return next;
  });
